import { useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from '@/domains/auth/hooks/authenticatedFetch';
import { useAuthContext } from '@/domains/auth/authContext';
import {
  clearIntercomJwt,
  isJwtUsable,
  loadIntercomJwt,
  saveIntercomJwt,
} from '../../utils/intercomJwtCache';

// JWT expires in 1h server-side; refresh 10min early so there's always headroom
const PROACTIVE_SKEW = 10 * 60; // seconds
const STALE_TIME = 50 * 60 * 1000; // 50min in ms

const intercomJwtQueryKey = (uid: string) => ['intercom-jwt', uid] as const;

export const useIntercomJwtQuery = () => {
  const { isLoggedIn, currentUser, isPending } = useAuthContext();
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const uid = currentUser?.uid ?? '';

  const query = useQuery({
    queryKey: intercomJwtQueryKey(uid),
    enabled: isLoggedIn && !isPending && !!uid,
    staleTime: STALE_TIME,
    refetchInterval: STALE_TIME,
    select: (token) => (isJwtUsable(token) ? token : undefined),
    queryFn: async (): Promise<string> => {
      const cached = await loadIntercomJwt();
      // Use PROACTIVE_SKEW so a scheduled refetch at t=50min fetches fresh
      // from the API rather than returning the still-technically-valid cached token
      if (cached && isJwtUsable(cached, undefined, PROACTIVE_SKEW))
        return cached;

      const response = await authenticatedFetch('/api/comms/intercom-jwt', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch Intercom JWT: ${response.status} ${response.statusText}`,
        );
      }
      const json = (await response.json()) as { token: string };
      if (!json.token) throw new Error('Intercom JWT missing in response');

      await saveIntercomJwt(json.token);
      return json.token;
    },
  });

  const refreshJwt = async (): Promise<string | undefined> => {
    if (!isLoggedIn || isPending || !uid) return undefined;
    await clearIntercomJwt();
    await queryClient.invalidateQueries({ queryKey: intercomJwtQueryKey(uid) });
    const result = await query.refetch();
    return result.data;
  };

  return { ...query, refreshJwt };
};
